import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { adminErrorService, ErrorLog, ErrorStatistics } from '@/services/adminErrorService';
import { toastHelper } from '@/utils/toastHelper';
import { RefreshCw, Search, AlertCircle, CheckCircle2, Eye, CheckSquare, Copy } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';

const ErrorLogs: React.FC = () => {
  const navigate = useNavigate();
  const [errors, setErrors] = useState<ErrorLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState<ErrorStatistics | null>(null);
  const [selectedError, setSelectedError] = useState<ErrorLog | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isResolveModalOpen, setIsResolveModalOpen] = useState(false);
  const [isBulkResolveModalOpen, setIsBulkResolveModalOpen] = useState(false);
  const [resolveNotes, setResolveNotes] = useState('');
  const [selectedErrorIds, setSelectedErrorIds] = useState<Set<string>>(new Set());
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 50;
  
  // Filter states
  const [userEmailSearch, setUserEmailSearch] = useState('');
  const [selectedErrorType, setSelectedErrorType] = useState<string>('');
  const [resolvedFilter, setResolvedFilter] = useState<string>('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const fetchErrors = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {
        limit: pageSize,
        offset: currentPage * pageSize
      };
      
      if (userEmailSearch) params.user_email_search = userEmailSearch;
      if (selectedErrorType) params.error_type = selectedErrorType;
      if (resolvedFilter !== '') params.resolved = resolvedFilter === 'true';
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      const data = await adminErrorService.getErrors(params);
      setErrors(data.errors);
      setTotalCount(data.total);
    } catch (error: any) {
      toastHelper.newMessage('error', 'Error', error.response?.data?.message || 'Failed to fetch error logs');
    } finally {
      setLoading(false);
    }
  }, [currentPage, userEmailSearch, selectedErrorType, resolvedFilter, startDate, endDate]);

  const fetchStatistics = useCallback(async () => {
    try {
      const stats = await adminErrorService.getErrorStatistics(
        startDate || undefined,
        endDate || undefined
      );
      setStatistics(stats);
    } catch (error: any) {
      console.error('Failed to fetch statistics:', error);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchErrors();
    fetchStatistics();
  }, [fetchErrors, fetchStatistics]);

  useEffect(() => {
    // Debounce search
    const timer = setTimeout(() => {
      setCurrentPage(0);
      fetchErrors();
    }, 300);
    return () => clearTimeout(timer);
  }, [userEmailSearch, selectedErrorType, resolvedFilter, startDate, endDate, fetchErrors]);

  useEffect(() => {
    fetchErrors();
  }, [currentPage, fetchErrors]);

  const handleViewError = async (errorId: string) => {
    try {
      const error = await adminErrorService.getErrorDetails(errorId);
      setSelectedError(error);
      setIsDetailModalOpen(true);
    } catch (error: any) {
      toastHelper.newMessage('error', 'Error', error.response?.data?.message || 'Failed to fetch error details');
    }
  };

  const handleResolveError = async () => {
    if (!selectedError) return;
    
    try {
      await adminErrorService.resolveError(selectedError.id, resolveNotes);
      toastHelper.newMessage('success', 'Success', 'Error marked as resolved');
      setIsResolveModalOpen(false);
      setIsDetailModalOpen(false);
      setResolveNotes('');
      setSelectedError(null);
      fetchErrors();
      fetchStatistics();
    } catch (error: any) {
      toastHelper.newMessage('error', 'Error', error.response?.data?.message || 'Failed to resolve error');
    }
  };

  const handleToggleSelect = (errorId: string) => {
    setSelectedErrorIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(errorId)) {
        newSet.delete(errorId);
      } else {
        newSet.add(errorId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const unresolvedErrors = errors.filter(e => !e.resolved);
    if (selectedErrorIds.size === unresolvedErrors.length) {
      setSelectedErrorIds(new Set());
    } else {
      setSelectedErrorIds(new Set(unresolvedErrors.map(e => e.id)));
    }
  };

  const handleBulkResolve = async () => {
    if (selectedErrorIds.size === 0) return;
    
    try {
      const result = await adminErrorService.bulkResolveErrors(Array.from(selectedErrorIds), resolveNotes);
      toastHelper.newMessage(
        'success', 
        'Success', 
        `Resolved ${result.resolved_count} error(s). ${result.not_found_count > 0 ? `${result.not_found_count} error(s) not found.` : ''}`
      );
      setIsBulkResolveModalOpen(false);
      setResolveNotes('');
      setSelectedErrorIds(new Set());
      fetchErrors();
      fetchStatistics();
    } catch (error: any) {
      toastHelper.newMessage('error', 'Error', error.response?.data?.message || 'Failed to resolve errors');
    }
  };

  const clearFilters = () => {
    setUserEmailSearch('');
    setSelectedErrorType('');
    setResolvedFilter('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(0);
  };

  const uniqueErrorTypes = useMemo(() => {
    const types = new Set<string>();
    errors.forEach(error => {
      types.add(error.error_type);
    });
    return Array.from(types).sort();
  }, [errors]);

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };


  const handleEmailClick = (_email: string, error: ErrorLog, emailIndex: number) => {
    // Find the corresponding user ID by index
    if (error.affected_user_ids && error.affected_user_ids.length > emailIndex) {
      const userId = error.affected_user_ids[emailIndex];
      navigate(`/admin/user-management/${userId}`, {
        state: { fromErrorLogs: true }
      });
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="space-y-4 sm:space-y-6 w-full max-w-none">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold">Error Logs</h2>
          <p className="text-sm sm:text-base text-muted-foreground">View and manage application errors</p>
        </div>
        <Button onClick={() => { fetchErrors(); fetchStatistics(); }} variant="outline" size="sm" className="self-start sm:self-auto">
          <RefreshCw className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Refresh</span>
        </Button>
      </div>

      {/* Statistics */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Total Errors</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{statistics.total_errors}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Unresolved</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">{statistics.unresolved_errors}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Resolved</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{statistics.resolved_errors}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Error Types</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{Object.keys(statistics.errors_by_type).length}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>Filter error logs by various criteria</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email-search">Search Email</Label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email-search"
                  placeholder="Search by email..."
                  value={userEmailSearch}
                  onChange={(e) => setUserEmailSearch(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="error-type">Error Type</Label>
              <Select value={selectedErrorType || "all"} onValueChange={(value) => setSelectedErrorType(value === "all" ? "" : value)}>
                <SelectTrigger id="error-type">
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {uniqueErrorTypes.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="resolved">Status</Label>
              <Select value={resolvedFilter || "all"} onValueChange={(value) => setResolvedFilter(value === "all" ? "" : value)}>
                <SelectTrigger id="resolved">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="false">Unresolved</SelectItem>
                  <SelectItem value="true">Resolved</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="start-date">Start Date</Label>
              <Input
                id="start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end-date">End Date</Label>
              <Input
                id="end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>
          <div className="mt-4">
            <Button onClick={clearFilters} variant="outline" size="sm">
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Error Logs Display */}
      {loading ? (
        <Card>
          <CardContent className="py-12 text-center">
            <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
            <p className="text-muted-foreground">Loading error logs...</p>
          </CardContent>
        </Card>
      ) : errors.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">No errors found</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="lg:hidden space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">Error Logs ({totalCount})</h3>
                <p className="text-sm text-muted-foreground">Showing {errors.length} errors</p>
              </div>
              {selectedErrorIds.size > 0 && (
                <Button
                  onClick={() => setIsBulkResolveModalOpen(true)}
                  variant="default"
                  size="sm"
                >
                  <CheckSquare className="h-4 w-4 mr-1" />
                  Resolve ({selectedErrorIds.size})
                </Button>
              )}
            </div>
            {errors.map((error) => (
              <Card
                key={error.id}
                className="cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => handleViewError(error.id)}
              >
                <CardContent className="p-4 space-y-3">
                  {/* Date and Select */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      {!error.resolved && (
                        <div onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedErrorIds.has(error.id)}
                            onCheckedChange={() => handleToggleSelect(error.id)}
                          />
                        </div>
                      )}
                      <div>
                        <p className="text-xs text-muted-foreground">Date</p>
                        <p className="text-sm font-medium">{formatDate(error.date)}</p>
                      </div>
                    </div>
                    {error.resolved ? (
                      <Badge variant="default" className="bg-green-600 text-xs flex-shrink-0">
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        Resolved
                      </Badge>
                    ) : (
                      <Badge variant="destructive" className="text-xs flex-shrink-0">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        Unresolved
                      </Badge>
                    )}
                  </div>

                  {/* Error Type and Count */}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Type</p>
                      <Badge variant="outline" className="text-xs">{error.error_type}</Badge>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Count</p>
                      <p className="font-semibold">{error.count}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs text-muted-foreground mb-1">Affected Users</p>
                      <p className="font-medium">{error.affected_user_count}</p>
                    </div>
                  </div>

                  {/* Error Message */}
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Message</p>
                    <p className="text-sm line-clamp-2">{error.error_message}</p>
                  </div>

                  {/* Affected Emails */}
                  {error.affected_user_emails && error.affected_user_emails.length > 0 && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Affected Emails</p>
                      <div className="flex flex-wrap gap-1">
                        {error.affected_user_emails.slice(0, 2).map((email, idx) => (
                          <Badge
                            key={idx}
                            variant="secondary"
                            className="text-xs cursor-pointer hover:bg-secondary/80"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEmailClick(email, error, idx);
                            }}
                          >
                            {email}
                          </Badge>
                        ))}
                        {error.affected_user_emails.length > 2 && (
                          <Badge variant="secondary" className="text-xs">
                            +{error.affected_user_emails.length - 2} more
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}

            {/* Mobile Pagination */}
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4">
                <div className="text-sm text-muted-foreground">
                  Page {currentPage + 1} of {totalPages}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                    disabled={currentPage === 0}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                    disabled={currentPage >= totalPages - 1}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Desktop Table View */}
          <Card className="w-full hidden lg:block">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Error Logs</CardTitle>
                  <CardDescription>
                    Showing {errors.length} of {totalCount} errors
                  </CardDescription>
                </div>
                {selectedErrorIds.size > 0 && (
                  <Button
                    onClick={() => setIsBulkResolveModalOpen(true)}
                    variant="default"
                    size="sm"
                  >
                    <CheckSquare className="h-4 w-4 mr-2" />
                    Resolve Selected ({selectedErrorIds.size})
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="w-full">
                <Table className="w-full">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12 px-3">
                        <Checkbox
                          checked={errors.filter(e => !e.resolved).length > 0 && selectedErrorIds.size === errors.filter(e => !e.resolved).length}
                          onCheckedChange={handleSelectAll}
                        />
                      </TableHead>
                      <TableHead className="w-[120px] px-3">Date</TableHead>
                      <TableHead className="w-[80px] px-3">Count</TableHead>
                      <TableHead className="w-[100px] px-3">Affected Users</TableHead>
                      <TableHead className="w-[200px] px-3">Affected Emails</TableHead>
                      <TableHead className="w-[160px] px-3">Error Type</TableHead>
                      <TableHead className="px-3">Message</TableHead>
                      <TableHead className="w-[120px] px-3">Status</TableHead>
                      <TableHead className="w-[80px] px-3">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {errors.map((error) => (
                      <TableRow key={error.id}>
                        <TableCell className="px-3">
                          {!error.resolved && (
                            <Checkbox
                              checked={selectedErrorIds.has(error.id)}
                              onCheckedChange={() => handleToggleSelect(error.id)}
                            />
                          )}
                        </TableCell>
                        <TableCell className="text-sm px-3 whitespace-nowrap">
                          {formatDate(error.date)}
                        </TableCell>
                        <TableCell className="text-sm px-3 whitespace-nowrap font-semibold">
                          {error.count}
                        </TableCell>
                        <TableCell className="text-sm px-3 whitespace-nowrap">
                          {error.affected_user_count}
                        </TableCell>
                        <TableCell className="px-3">
                          <div className="flex flex-wrap gap-1 max-w-[200px]">
                            {error.affected_user_emails && error.affected_user_emails.length > 0 ? (
                              error.affected_user_emails.slice(0, 2).map((email, sliceIdx) => {
                                const originalIdx = sliceIdx; // Since we're slicing from 0, the index matches
                                return (
                                  <Badge 
                                    key={originalIdx} 
                                    variant="secondary" 
                                    className="text-xs cursor-pointer hover:bg-secondary/80 transition-colors"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEmailClick(email, error, originalIdx);
                                    }}
                                  >
                                    {email}
                                  </Badge>
                                );
                              })
                            ) : (
                              <span className="text-xs text-muted-foreground">-</span>
                            )}
                            {error.affected_user_emails && error.affected_user_emails.length > 2 && (
                              <Badge variant="secondary" className="text-xs">
                                +{error.affected_user_emails.length - 2} more
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="px-3 whitespace-nowrap">
                          <Badge variant="outline" className="text-xs">{error.error_type}</Badge>
                        </TableCell>
                        <TableCell className="px-3">
                          <div className="text-sm break-words max-w-none" title={error.error_message}>
                            {error.error_message}
                          </div>
                        </TableCell>
                        <TableCell className="px-3 whitespace-nowrap">
                          {error.resolved ? (
                            <Badge variant="default" className="bg-green-600 text-xs">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Resolved
                            </Badge>
                          ) : (
                            <Badge variant="destructive" className="text-xs">
                              <AlertCircle className="h-3 w-3 mr-1" />
                              Unresolved
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="px-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewError(error.id)}
                            className="h-8 w-8 p-0"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Desktop Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 px-6 pb-4">
                  <div className="text-sm text-muted-foreground">
                    Page {currentPage + 1} of {totalPages}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                      disabled={currentPage === 0}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                      disabled={currentPage >= totalPages - 1}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Error Detail Modal */}
      <Dialog open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Error Details</DialogTitle>
            <DialogDescription>
              Detailed information about the error
            </DialogDescription>
          </DialogHeader>
          {selectedError && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Error ID</Label>
                  <p className="text-sm font-mono">{selectedError.id}</p>
                </div>
                <div>
                  <Label>Date</Label>
                  <p className="text-sm">{formatDate(selectedError.date)}</p>
                </div>
                <div>
                  <Label>Occurrence Count</Label>
                  <p className="text-sm font-semibold">{selectedError.count}</p>
                </div>
                <div>
                  <Label>Affected Users</Label>
                  <p className="text-sm font-semibold">{selectedError.affected_user_count}</p>
                </div>
                <div>
                  <Label>First Occurrence</Label>
                  <p className="text-sm">{formatTimestamp(selectedError.first_occurrence)}</p>
                </div>
                <div>
                  <Label>Last Occurrence</Label>
                  <p className="text-sm">{formatTimestamp(selectedError.last_occurrence)}</p>
                </div>
                <div>
                  <Label>Error Type</Label>
                  <Badge variant="outline">{selectedError.error_type}</Badge>
                </div>
                <div>
                  <Label>Status Code</Label>
                  <p className="text-sm">{selectedError.status_code || 'N/A'}</p>
                </div>
                <div>
                  <Label>Status</Label>
                  {selectedError.resolved ? (
                    <Badge variant="default" className="bg-green-600">Resolved</Badge>
                  ) : (
                    <Badge variant="destructive">Unresolved</Badge>
                  )}
                </div>
                <div>
                  <Label>Request Path</Label>
                  <p className="text-sm font-mono">{selectedError.request_path || 'N/A'}</p>
                </div>
                <div>
                  <Label>Request Method</Label>
                  <p className="text-sm">{selectedError.request_method || 'N/A'}</p>
                </div>
              </div>
              {selectedError.affected_user_ids && selectedError.affected_user_ids.length > 0 && (
                <div>
                  <Label>Affected User IDs ({selectedError.affected_user_ids.length})</Label>
                  <div className="mt-1 p-3 bg-muted rounded-md max-h-48 overflow-y-auto">
                    <div className="flex flex-wrap gap-2">
                      {selectedError.affected_user_ids.map((userId, index) => (
                        <span key={index} className="text-xs font-mono bg-background px-2 py-1 rounded">
                          {userId}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {selectedError.affected_user_emails && selectedError.affected_user_emails.length > 0 && (
                <div>
                  <Label>Affected User Emails ({selectedError.affected_user_emails.length})</Label>
                  <div className="mt-1 p-3 bg-muted rounded-md max-h-48 overflow-y-auto">
                    <div className="flex flex-wrap gap-2">
                      {selectedError.affected_user_emails.map((email, index) => (
                        <Badge 
                          key={index} 
                          variant="secondary" 
                          className="text-xs cursor-pointer hover:bg-secondary/80 transition-colors"
                          onClick={() => {
                            handleEmailClick(email, selectedError, index);
                            setIsDetailModalOpen(false);
                          }}
                        >
                          {email}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              <div>
                <Label>Error Message</Label>
                <div className="mt-1 p-3 bg-muted rounded-md">
                  <pre className="text-sm whitespace-pre-wrap">{selectedError.error_message}</pre>
                </div>
              </div>
              {selectedError.sample_stack_trace && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label>Sample Stack Trace</Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(selectedError.sample_stack_trace || '');
                          toastHelper.newMessage('success', 'Copied', 'Stack trace copied to clipboard');
                        } catch (error) {
                          toastHelper.newMessage('error', 'Error', 'Failed to copy stack trace');
                        }
                      }}
                      title="Copy stack trace"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="mt-1 p-3 bg-muted rounded-md max-h-64 overflow-y-auto">
                    <pre className="text-xs whitespace-pre-wrap font-mono">{selectedError.sample_stack_trace}</pre>
                  </div>
                </div>
              )}
              {/* Request body display disabled - uncomment if needed in future
              {false && selectedError?.request_body && (
                <div>
                  <Label>Request Body</Label>
                  <div className="mt-1 p-3 bg-muted rounded-md max-h-64 overflow-y-auto">
                    <pre className="text-xs whitespace-pre-wrap font-mono">
                      {JSON.stringify(selectedError.request_body, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
              */}
              {selectedError.notes && (
                <div>
                  <Label>Notes</Label>
                  <div className="mt-1 p-3 bg-muted rounded-md">
                    <p className="text-sm">{selectedError.notes}</p>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            {selectedError && !selectedError.resolved && (
              <Button
                onClick={() => {
                  setIsResolveModalOpen(true);
                }}
              >
                Mark as Resolved
              </Button>
            )}
            <Button variant="outline" onClick={() => setIsDetailModalOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resolve Error Modal */}
      <Dialog open={isResolveModalOpen} onOpenChange={setIsResolveModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolve Error</DialogTitle>
            <DialogDescription>
              Add notes about how this error was resolved (optional)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="resolve-notes">Notes</Label>
              <Textarea
                id="resolve-notes"
                placeholder="Enter resolution notes..."
                value={resolveNotes}
                onChange={(e) => setResolveNotes(e.target.value)}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsResolveModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleResolveError}>
              Mark as Resolved
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Resolve Error Modal */}
      <Dialog open={isBulkResolveModalOpen} onOpenChange={setIsBulkResolveModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolve {selectedErrorIds.size} Error(s)</DialogTitle>
            <DialogDescription>
              Add notes about how these errors were resolved (optional)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="bulk-resolve-notes">Notes</Label>
              <Textarea
                id="bulk-resolve-notes"
                placeholder="Enter resolution notes..."
                value={resolveNotes}
                onChange={(e) => setResolveNotes(e.target.value)}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsBulkResolveModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleBulkResolve}>
              Mark {selectedErrorIds.size} as Resolved
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ErrorLogs;

